import React, { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { Radio, BookOpen, ShieldCheck, GraduationCap, Trophy, Gift, ArrowRight, Crown, ArrowUp, ArrowDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Reveal from "../components/common/Reveal";
import ChartBackdrop from "../components/common/ChartBackdrop";
import LiveTraders from "../components/common/LiveTraders";
import { getSignals } from "../services/signals";

const FEATURES = [
  { to: "/signals", icon: Radio, title: "AI Signals", desc: "Live UP / DOWN calls across every Quotex pair with confidence." },
  { to: "/journal", icon: BookOpen, title: "Trading Journal", desc: "Log setups, results and lessons. Review decisions, not just P&L." },
  { to: "/risk-management", icon: ShieldCheck, title: "Risk Management", desc: "Size every trade and protect your capital with a fixed-risk model." },
  { to: "/daily-lesson", icon: GraduationCap, title: "Daily AI Lesson", desc: "A focused daily reading on discipline and decision quality." },
  { to: "/leaderboard", icon: Trophy, title: "Leaderboard", desc: "The most disciplined traders ranked by verified performance." },
  { to: "/giveaway", icon: Gift, title: "Giveaway", desc: "Enter the daily reward pool with a live countdown." },
];

function Ticker() {
  const [sig, setSig] = useState([]);
  useEffect(() => {
    const load = () => getSignals(8).then((d) => setSig(d.signals)).catch(() => {});
    load(); const t = setInterval(load, 60000); return () => clearInterval(t);
  }, []);
  if (!sig.length) return null;
  const list = [...sig, ...sig];
  return (
    <div className="relative overflow-hidden border-y border-white/8 bg-[#0c0c0e]">
      <div className="flex animate-marquee whitespace-nowrap py-2.5">
        {list.map((s, i) => {
          const up = s.direction === "UP";
          return (
            <span key={i} className="mx-5 inline-flex items-center gap-2 font-mono text-[11px] tracking-wide text-neutral-400">
              <span className="text-white">{s.pair}</span>
              <span className={up ? "text-[#8fd6b4]" : "text-[#e0949c]"}>{up ? <ArrowUp size={11} className="inline" /> : <ArrowDown size={11} className="inline" />} {s.direction}</span>
              <span className="text-neutral-600">{s.confidence}%</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { openVip } = useOutletContext() || {};

  return (
    <div className="mx-auto max-w-6xl">
      <Reveal>
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <LiveTraders />
          <span className="font-mono text-[11px] uppercase tracking-widest text-neutral-500">Private Trading Workspace</span>
        </div>
      </Reveal>

      {/* Hero */}
      <div className="surface relative mt-4 overflow-hidden rounded-3xl">
        <ChartBackdrop opacity={0.18} />
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-white/[0.05] blur-[120px]" />
        <div className="relative z-10 p-8 md:p-14">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/30 px-3 py-1.5 backdrop-blur">
            <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-[#8fd6b4]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-300">Live · Welcome, {user?.name?.split(" ")[0] || "Trader"}</span>
          </motion.div>

          <h1 className="brand-pop brand-glow font-head text-4xl font-black uppercase leading-[0.92] tracking-tight md:text-6xl">
            <span className="text-silver">SANJAY LIMITLESS</span>
          </h1>
          <p className="mt-3 font-head text-lg font-semibold uppercase tracking-tight text-neutral-300 md:text-2xl">
            Clarity before every decision.
          </p>
          <p className="mt-6 max-w-xl leading-relaxed text-neutral-400">
            A private terminal for disciplined trading — a live signal room, a structured
            journal, a risk engine and daily lessons in one calm, premium interface. Built to
            help you think clearly, not to promise profits.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={openVip}
              className="btn-vip flex items-center gap-2 px-7 py-3.5 font-mono text-xs font-bold uppercase tracking-widest transition-transform hover:-translate-y-0.5"
              data-testid="home-vip-btn"
            >
              <Crown size={14} /> Join Sanjay VIP
            </button>
            <button onClick={() => navigate("/journal")} className="btn-ghost sweep flex items-center gap-2 rounded-xl px-7 py-3.5 font-mono text-xs uppercase tracking-widest transition-transform hover:-translate-y-0.5" data-testid="home-journal-btn">
              Open Journal
            </button>
          </div>
        </div>
      </div>

      {/* live ticker */}
      <div className="mt-6 overflow-hidden rounded-xl"><Ticker /></div>

      {/* Feature previews */}
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map((f, i) => (
          <Reveal key={f.to} delay={i * 0.05}>
            <button onClick={() => navigate(f.to)}
              className="surface ring-hover group flex h-full w-full flex-col items-start rounded-2xl p-6 text-left"
              data-testid={`home-feature-${f.title.toLowerCase().replace(/\s/g, "-")}`}>
              <span className="mb-5 grid h-11 w-11 place-items-center rounded-xl border border-white/10 bg-white/[0.03] text-[#d4d8dc] transition-colors group-hover:border-white/25">
                <f.icon size={19} />
              </span>
              <h3 className="font-head text-base font-bold uppercase tracking-tight text-white">{f.title}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-neutral-400">{f.desc}</p>
              <span className="mt-5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-neutral-500 transition-colors group-hover:text-white">
                View <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
              </span>
            </button>
          </Reveal>
        ))}
      </div>
    </div>
  );
}
